import { useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { buildAllowedLangOptions, type LangOption } from '../utils/allowedLangOptions';
import { normalizeLang } from '../utils/normalizeLang';
import css from './LangSelector.module.less';

export type { LangOption } from '../utils/allowedLangOptions';

// normalizeLang 已抽到独立 util,这里 re-export 保持现有调用方
// `import { normalizeLang } from './LangSelector'` 不需要改。
export { normalizeLang } from '../utils/normalizeLang';

// 观众端总语言数 cap = 4 (allowed ∪ audience_extras), 与后端 MAX_AUDIENCE_LANGS_TOTAL 一致
const TOTAL_CAP = 4;

interface LangSelectorProps {
  allOptions: LangOption[];
  desktopLangs: string[];                  // 桌面端在翻的语言(锁定, 保证)
  allowedLangs: string[];                  // 保证集: 桌面端 + 主播固定的语言(永远可选, 不占竞争名额)
  audienceLangs: string[];                 // 观众已订阅的语言
  /** 是否还有空槽(allowed 未占满上限): true = allowed 之外的语言可先到先得; false = 只能在 allowed 内切 */
  freeChoice: boolean;
  selectedLang: string;                    // 当前选定查看的语言
  onSelectLang: (lang: string) => void;    // 切换查看语言
  /** 先到先得订阅 allowed 之外的语言; releaseLang = 满员换语言时要释放的旧语言。返回是否成功 */
  onSubscribe: (lang: string, releaseLang?: string) => Promise<boolean>;
}

/**
 * 观众端语言选择器(2026-09 redesign, 主播端取消"自由/受控"开关, 改为一排 4 名额):
 * - 保证集 allowedLangs(桌面端在翻语言 + 主播固定语言): 永远可选、不占竞争名额, 分
 *   "桌面端已翻译 / 主播已固定"两组置顶。
 * - 还有空槽时(freeChoice=true): allowed 之外的语言列在"追加语言", 观众先到先得,
 *   合计(allowed ∪ 观众订阅)≤4, 观众间互相挤占, 满员可换掉自己正在看的先到先得语言。
 * - 无空槽时(freeChoice=false, allowed 占满 4): 只列 allowed, 观众只能在其中切换。
 * 底部始终提示"需要别的语言找主持人"。
 */
export function LangSelector({
  allOptions,
  desktopLangs,
  allowedLangs,
  audienceLangs,
  freeChoice,
  selectedLang,
  onSelectLang,
  onSubscribe,
}: LangSelectorProps) {
  const { t } = useTranslation();

  // 所有集合用 normalized key (避免 zh_hans 跟 zh 不匹配的问题)
  const desktopSet = useMemo(
    () => new Set(desktopLangs.map(normalizeLang)),
    [desktopLangs],
  );
  const allowedSet = useMemo(
    () => new Set(allowedLangs.map(normalizeLang)),
    [allowedLangs],
  );
  const audienceSet = useMemo(
    () => new Set(audienceLangs.map(normalizeLang)),
    [audienceLangs],
  );

  // 有空槽: 全部语言可见(allowed 之外的可先到先得); 无空槽: 只列 allowed
  const options = useMemo(
    () => (freeChoice ? allOptions : buildAllowedLangOptions(allowedLangs, allOptions)),
    [freeChoice, allowedLangs, allOptions],
  );
  // 三组: 桌面端(锁定) / 主播固定 / 先到先得(allowed 之外)
  const desktopGroup = options.filter((o) => desktopSet.has(normalizeLang(o.value)));
  const fixedGroup = options.filter(
    (o) => !desktopSet.has(normalizeLang(o.value)) && allowedSet.has(normalizeLang(o.value)),
  );
  const freeGroup = options.filter((o) => !allowedSet.has(normalizeLang(o.value)));

  // ---- 名额逻辑(保证集恒占名额, 观众先到先得占剩余空槽) ----
  const currentTotal = useMemo(() => {
    const union = new Set<string>();
    allowedSet.forEach((l) => union.add(l));
    audienceSet.forEach((l) => union.add(l));
    return union.size;
  }, [allowedSet, audienceSet]);
  const limitReached = freeChoice && currentTotal >= TOTAL_CAP;
  // 满员时能否"换语言": 自己当前查看的是先到先得(allowed 之外)语言。保证集内的不占名额, 换走腾不出位置。
  // 前端不知道该语言是否还有其他观众在用 —— 交给后端原子判定:换不出名额会拒绝并恢复原订阅。
  const canSwap = !!selectedLang && !allowedSet.has(normalizeLang(selectedLang));

  const handleClick = async (opt: LangOption, isGuaranteed: boolean) => {
    if (isGuaranteed) {
      // 保证集(桌面端 + 主播固定): 点选即切换, index.tsx 的 effect 会 subscribe(驱动翻译/历史补翻), 不占名额
      onSelectLang(opt.value);
      return;
    }
    const subscribed = audienceSet.has(normalizeLang(opt.value));
    if (subscribed) {
      onSelectLang(opt.value);
      return;
    }
    if (limitReached) {
      if (!canSwap) {
        message.warning(
          t('share.lang.limitReachedToast', {
            defaultValue: 'Limit reached, cannot subscribe to more languages',
          }),
        );
        return;
      }
      // 满员换语言: 后端原子完成"释放旧语言→检查名额→订阅新语言",失败保持原语言(toast 在上层)
      const ok = await onSubscribe(opt.value, selectedLang);
      if (ok) onSelectLang(opt.value);
      return;
    }
    onSubscribe(opt.value);
    onSelectLang(opt.value);
  };

  const renderItem = (opt: LangOption, isGuaranteed: boolean, isDesktop: boolean) => {
    const isSelected = normalizeLang(selectedLang) === normalizeLang(opt.value);
    const subscribed = audienceSet.has(normalizeLang(opt.value));
    const inUse = !isGuaranteed && !isSelected && subscribed;
    // 满员且不可换时锁死未订阅的先到先得项
    const locked = freeChoice && !isGuaranteed && !subscribed && limitReached && !canSwap;
    return (
      <div
        key={opt.value}
        className={clsx(css.item, isDesktop && css.desktop, isSelected && css.selected, locked && css.locked)}
        onClick={() => handleClick(opt, isGuaranteed)}
      >
        <span className={css.name}>{opt.label}</span>
        {isDesktop && (
          <span className={css.tag}>{t('share.lang.tagDesktop', { defaultValue: "Host's" })}</span>
        )}
        {/* 高亮只给"自己正在看"的语言;其他观众订阅的语言用右侧小字标记区分 */}
        {inUse && (
          <span className={css.inUseTag}>
            {t('share.lang.tagInUse', { defaultValue: 'In use' })}
          </span>
        )}
        {locked && (
          <span className={css.lockHint}>
            {t('share.lang.limitReached', { defaultValue: 'Limit reached' })}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={css.selector}>
      <div className={css.fixedTop}>
        {desktopGroup.length > 0 && (
          <>
            <div className={css.groupHeader}>
              {t('share.lang.desktopGroup', { defaultValue: 'Already translated by host' })}
            </div>
            {desktopGroup.map((opt) => renderItem(opt, true, true))}
          </>
        )}
        {fixedGroup.length > 0 && (
          <>
            <div className={css.groupHeader}>
              {t('share.lang.fixedGroup', { defaultValue: 'Pinned by host' })}
            </div>
            {fixedGroup.map((opt) => renderItem(opt, true, false))}
          </>
        )}
        {freeGroup.length > 0 && (
          <div className={css.groupHeader}>
            {t('share.lang.extendGroup', { defaultValue: 'Additional languages' })}
            {freeChoice && (
              <span className={css.limit}>
                {t('share.lang.totalLimit', {
                  count: currentTotal,
                  defaultValue: `(${currentTotal}/${TOTAL_CAP} total)`,
                })}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 中间可滚动: 先到先得(allowed 之外)的全部其它语言 */}
      <div className={css.scrollList}>
        {freeGroup.map((opt) => renderItem(opt, false, false))}
      </div>

      {/* 底部固定静态提示: 需要更多语言找主持人 */}
      <div className={css.askHost}>
        {t('share.lang.askHost', {
          defaultValue: "Don't see your language? Ask the meeting host to add it.",
        })}
      </div>
    </div>
  );
}
